/**
 * Outreach Leads page – table, filters, search, edit, import (Excel & CSV).
 */
import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import {
  Table, Input, InputNumber, Button, Space, Tag, Drawer, Form, Select, message,
  Upload, Modal, Steps, Card, Typography, Alert, Tooltip, Spin,
  DatePicker, Tabs, Checkbox, Pagination,
} from 'antd';
import { SearchOutlined, EditOutlined, UploadOutlined, DownloadOutlined, CheckOutlined, InfoCircleOutlined, LoadingOutlined, UserAddOutlined, FilterOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Lead, LeadCreate, LeadUpdate, ColumnInspectionResponse, ImportPreviewResponse, ImportMetadata } from '../types';
import { outreachLeadsApi } from '../api';
import { pruneMultiSelect, useCascadingFilterOptions } from '../hooks/useCascadingFilterOptions';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useTheme } from '../../../contexts/ThemeContext';
import { getLeadStatusLabel, leadToStatusFilter, renderLeadStatusTag, type LeadStatusFilter } from '../utils/leadStatus';

const { TextArea } = Input;
const { Text } = Typography;

const LAST_CONTACTED_OPTIONS = [
  { value: 3, label: 'Not in last 3 days' },
  { value: 7, label: 'Not in last 7 days' },
  { value: 14, label: 'Not in last 14 days' },
  { value: 30, label: 'Not in last 30 days' },
  { value: 60, label: 'Not in last 60 days' },
  { value: 90, label: 'Not in last 90 days' },
] as const;

type LeadColumnKey =
  | 'email' | 'name' | 'company' | 'role' | 'domain' | 'tags' | 'status' | 'actions'
  | 'country' | 'city' | 'therapy_interest' | 'lead_source' | 'added_by'
  | 'lead_potential' | 'last_contacted_at' | 'total_emails_sent' | 'next_followup_at';

interface ResizableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: number;
  onResizeStart?: (event: React.MouseEvent<HTMLSpanElement>) => void;
}

const DEFAULT_COLUMN_WIDTHS: Record<LeadColumnKey, number> = {
  email: 220,
  name: 150,
  company: 140,
  role: 150,
  domain: 110,
  tags: 120,
  status: 130,
  actions: 100,
  country: 100,
  city: 100,
  therapy_interest: 150,
  lead_source: 110,
  added_by: 110,
  lead_potential: 110,
  last_contacted_at: 130,
  total_emails_sent: 90,
  next_followup_at: 130,
};

const cellEllipsisStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const LEADS_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500];
/** Use virtual scroll + lighter cells above this page size */
const VIRTUAL_TABLE_THRESHOLD = 50;

function computeTableScrollY(): number {
  if (typeof window === 'undefined') return 520;
  return Math.max(400, Math.min(680, window.innerHeight - 320));
}

/** Virtual table body scroll does not always sync the header; align explicitly. */
function syncTableHeaderScrollLeft(wrapper: HTMLElement, scrollLeft: number): void {
  const header = wrapper.querySelector('.ant-table-header') as HTMLDivElement | null;
  if (header && header.scrollLeft !== scrollLeft) {
    header.scrollLeft = scrollLeft;
  }
}

function renderEllipsisCell(text: string, compact: boolean): React.ReactNode {
  if (compact) {
    return <span style={cellEllipsisStyle} title={text === '-' ? undefined : text}>{text}</span>;
  }
  return <Tooltip title={text}><span style={cellEllipsisStyle}>{text}</span></Tooltip>;
}

function renderJoinedListCell(values: string[] | null | undefined): React.ReactNode {
  if (!values?.length) return '-';
  const text = values.join(', ');
  return <span style={cellEllipsisStyle} title={text}>{text}</span>;
}

const LEAD_POTENTIAL_OPTIONS = ['low', 'medium', 'high', 'strategic'] as const;
const LEAD_SOURCE_OPTIONS = [
  'linkedin', 'conference', 'scraped', 'referral', 'inbound', 'manual', 'import', 'hunter_api',
] as const;
const THERAPY_INTEREST_OPTIONS = ['dermatology', 'psoriasis', 'skin cancer', 'glaucoma', 'retinal', 'ophthalmology'] as const;
const ADDED_BY_OPTIONS = ['Faaiz', 'Bushra', 'Raza'] as const;

const LEAD_POTENTIAL_COLORS: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  strategic: 'purple',
};

/** Simple email validation – checks for @ and a domain with a dot */
const isValidEmail = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  // Must have @, something before it, and a domain with at least one dot
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

/** Display label for lead field options */
const FIELD_LABELS: Record<string, string> = {
  email: 'Email (required)',
  first_name: 'First Name',
  last_name: 'Last Name',
  company: 'Company',
  role: 'Role',
  domain: 'Domain (e.g. sunpharma.com)',
  country: 'Country',
  city: 'City',
  therapy_interest: 'Therapy Interest',
  lead_source: 'Lead Source',
  lead_potential: 'Lead Potential',
  added_by: 'Added By',
  tags: 'Tags',
  notes: 'Notes',
};

/** Use default th for selection column — ResizableHeaderCell breaks checkbox header layout. */
const ResizableTableHeaderCell: React.FC<ResizableHeaderCellProps> = (props) => {
  const { className } = props;
  if (className?.includes('ant-table-selection-column')) {
    const { width: _w, onResizeStart: _r, ...thProps } = props;
    return <th {...thProps} className={className} />;
  }
  return <ResizableHeaderCell {...props} />;
};

const ResizableHeaderCell: React.FC<ResizableHeaderCellProps> = ({
  width,
  onResizeStart,
  children,
  style,
  ...restProps
}) => {
  const [isResizing, setIsResizing] = React.useState(false);
  
  const handleMouseDown = (e: React.MouseEvent<HTMLSpanElement>) => {
    setIsResizing(true);
    onResizeStart?.(e);
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th
      {...restProps}
      style={{
        ...style,
        width,
        minWidth: width,
        position: 'relative',
        whiteSpace: 'nowrap',
        padding: '12px 8px', // Standardized padding
      }}
    >
      <span style={{ display: 'inline-block', paddingRight: onResizeStart ? 12 : 0, whiteSpace: 'nowrap' }}>
        {children}
      </span>
      {onResizeStart && (
        <span
          onMouseDown={handleMouseDown}
          className={`column-resize-handle ${isResizing ? 'is-resizing' : ''}`}
          style={{
            position: 'absolute',
            top: 0,
            right: -2,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            userSelect: 'none',
            zIndex: 10,
            transition: 'background 0.2s',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              width: 2,
              height: '100%',
              background: isResizing ? '#1890ff' : '#f0f0f0',
              transition: 'background 0.2s',
            }}
            className="handle-bar"
          />
        </span>
      )}
    </th>
  );
};

export const OutreachLeads: React.FC = () => {
  const { isDark } = useTheme();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [doNotSendFilter, setDoNotSendFilter] = useState<boolean | undefined>(undefined);
  const [isSuppressedFilter, setIsSuppressedFilter] = useState<boolean | undefined>(undefined);
  const [countryFilter, setCountryFilter] = useState<string[] | undefined>(undefined);
  const [cityFilter, setCityFilter] = useState<string[] | undefined>(undefined);
  const [therapyFilter, setTherapyFilter] = useState<string[] | undefined>(undefined);
  const [leadSourceFilter, setLeadSourceFilter] = useState<string[] | undefined>(undefined);
  const [leadPotentialFilter, setLeadPotentialFilter] = useState<string[] | undefined>(undefined);
  const [addedByFilter, setAddedByFilter] = useState<string[] | undefined>(undefined);
  const [roleFilter, setRoleFilter] = useState<string[] | undefined>(undefined);
  const [domainFilter, setDomainFilter] = useState<string[] | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>('active');
  const [lastContactedDaysFilter, setLastContactedDaysFilter] = useState<number | undefined>(undefined);
  const [minEmailsSentFilter, setMinEmailsSentFilter] = useState<number | undefined>(undefined);
  const [maxEmailsSentFilter, setMaxEmailsSentFilter] = useState<number | undefined>(undefined);
  const [tagsFilter, setTagsFilter] = useState<string[] | undefined>(undefined);
  // Exclude filters
  const [excludeLeadSource, setExcludeLeadSource] = useState<string[] | undefined>(undefined);
  const [excludeLeadPotential, setExcludeLeadPotential] = useState<string[] | undefined>(undefined);
  const [excludeAddedBy, setExcludeAddedBy] = useState<string[] | undefined>(undefined);
  const [excludeCountry, setExcludeCountry] = useState<string[] | undefined>(undefined);
  const [excludeCity, setExcludeCity] = useState<string[] | undefined>(undefined);
  const [excludeRole, setExcludeRole] = useState<string[] | undefined>(undefined);
  const [excludeDomain, setExcludeDomain] = useState<string[] | undefined>(undefined);
  const [excludeTags, setExcludeTags] = useState<string[] | undefined>(undefined);
  const [excludeTherapy, setExcludeTherapy] = useState<string[] | undefined>(undefined);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editForm] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [columnInspection, setColumnInspection] = useState<ColumnInspectionResponse | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [mappingForm] = Form.useForm();
  const [metadataForm] = Form.useForm();
  const [mappedFields, setMappedFields] = useState<Record<string, string>>({}); // Track which field is mapped to which column
  const [invalidEmails, setInvalidEmails] = useState<{ row: number; original: string; corrected?: string }[]>([]);
  const [correctedDbDuplicates, setCorrectedDbDuplicates] = useState<Set<string>>(new Set());
  const emailCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addedBySet, setAddedBySet] = useState(false);
  const [previewData, setPreviewData] = useState<ImportPreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<LeadColumnKey, number>>(DEFAULT_COLUMN_WIDTHS);
  const [importMode, setImportMode] = useState<'file' | 'manual'>('file');
  const [manualForm] = Form.useForm();
  const [manualLoading, setManualLoading] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
  const [bulkEditField, setBulkEditField] = useState<string | undefined>(undefined);
  const [bulkEditForm] = Form.useForm();
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [isTableOverflowing, setIsTableOverflowing] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const fakeScrollRef = useRef<HTMLDivElement>(null);
  const fakeScrollInnerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);
  const isSyncingScroll = useRef(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [tableScrollY, setTableScrollY] = useState(computeTableScrollY);

  const useVirtualTable = pageSize > VIRTUAL_TABLE_THRESHOLD;
  const useCompactCells = useVirtualTable;

  useEffect(() => {
    if (!useVirtualTable) return;
    const onResize = () => setTableScrollY(computeTableScrollY());
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [useVirtualTable]);

  const filterOptionsParams = useMemo(() => ({
    search: search || undefined,
    do_not_send: doNotSendFilter,
    is_suppressed: isSuppressedFilter,
    country: countryFilter,
    city: cityFilter,
    therapy_interest: therapyFilter,
    lead_source: leadSourceFilter,
    lead_potential: leadPotentialFilter,
    added_by: addedByFilter,
    role: roleFilter,
    domain: domainFilter,
    tags: tagsFilter,
    status: statusFilter,
    last_contacted_days_ago: lastContactedDaysFilter,
    min_emails_sent: minEmailsSentFilter,
    max_emails_sent: maxEmailsSentFilter,
    exclude_lead_source: excludeLeadSource,
    exclude_lead_potential: excludeLeadPotential,
    exclude_added_by: excludeAddedBy,
    exclude_country: excludeCountry,
    exclude_city: excludeCity,
    exclude_role: excludeRole,
    exclude_domain: excludeDomain,
    exclude_tags: excludeTags,
    exclude_therapy_interest: excludeTherapy,
  }), [search, doNotSendFilter, isSuppressedFilter, countryFilter, cityFilter, therapyFilter,
    leadSourceFilter, leadPotentialFilter, addedByFilter, roleFilter, domainFilter, tagsFilter,
    statusFilter, lastContactedDaysFilter, minEmailsSentFilter, maxEmailsSentFilter,
    excludeLeadSource, excludeLeadPotential, excludeAddedBy, excludeCountry, excludeCity,
    excludeRole, excludeDomain, excludeTags, excludeTherapy]);

  const { filterOptions, loading: filterOptionsLoading } = useCascadingFilterOptions(filterOptionsParams);

  useEffect(() => {
    setTherapyFilter((v) => pruneMultiSelect(v, filterOptions.therapy_interest));
    setAddedByFilter((v) => pruneMultiSelect(v, filterOptions.added_by));
    setLeadSourceFilter((v) => pruneMultiSelect(v, filterOptions.lead_source));
    setLeadPotentialFilter((v) => pruneMultiSelect(v, filterOptions.lead_potential));
    setRoleFilter((v) => pruneMultiSelect(v, filterOptions.roles));
    setDomainFilter((v) => pruneMultiSelect(v, filterOptions.domains));
    setCountryFilter((v) => pruneMultiSelect(v, filterOptions.countries));
    setCityFilter((v) => pruneMultiSelect(v, filterOptions.cities));
    setTagsFilter((v) => pruneMultiSelect(v, filterOptions.tags));
    setExcludeTherapy((v) => pruneMultiSelect(v, filterOptions.therapy_interest));
    setExcludeAddedBy((v) => pruneMultiSelect(v, filterOptions.added_by));
    setExcludeLeadSource((v) => pruneMultiSelect(v, filterOptions.lead_source));
    setExcludeLeadPotential((v) => pruneMultiSelect(v, filterOptions.lead_potential));
    setExcludeRole((v) => pruneMultiSelect(v, filterOptions.roles));
    setExcludeDomain((v) => pruneMultiSelect(v, filterOptions.domains));
    setExcludeCountry((v) => pruneMultiSelect(v, filterOptions.countries));
    setExcludeCity((v) => pruneMultiSelect(v, filterOptions.cities));
    setExcludeTags((v) => pruneMultiSelect(v, filterOptions.tags));
  }, [filterOptions]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await outreachLeadsApi.list({
        page, page_size: pageSize, search: search || undefined,
        do_not_send: doNotSendFilter, is_suppressed: isSuppressedFilter,
        country: countryFilter, city: cityFilter,
        therapy_interest: therapyFilter, lead_source: leadSourceFilter,
        lead_potential: leadPotentialFilter, added_by: addedByFilter,
        role: roleFilter, domain: domainFilter,
        tags: tagsFilter,
        status: statusFilter,
        last_contacted_days_ago: lastContactedDaysFilter,
        min_emails_sent: minEmailsSentFilter, max_emails_sent: maxEmailsSentFilter,
        exclude_lead_source: excludeLeadSource,
        exclude_lead_potential: excludeLeadPotential,
        exclude_added_by: excludeAddedBy,
        exclude_country: excludeCountry,
        exclude_city: excludeCity,
        exclude_role: excludeRole,
        exclude_domain: excludeDomain,
        exclude_tags: excludeTags,
        exclude_therapy_interest: excludeTherapy,
      });
      setLeads(res.items); setTotal(res.total);
    } catch { message.error('Failed to fetch leads'); }
    finally { setLoading(false); }
  }, [page, pageSize, search, doNotSendFilter, isSuppressedFilter,
    countryFilter, cityFilter, therapyFilter, leadSourceFilter, leadPotentialFilter,
    addedByFilter, roleFilter, domainFilter, tagsFilter, statusFilter,
    lastContactedDaysFilter, minEmailsSentFilter, maxEmailsSentFilter,
    excludeLeadSource, excludeLeadPotential, excludeAddedBy, excludeCountry,
    excludeCity, excludeRole, excludeDomain, excludeTags, excludeTherapy]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Virtual table keeps vertical offset across page changes; reset after each page load
  useEffect(() => {
    if (!useVirtualTable || loading) return;
    const frame = requestAnimationFrame(() => {
      try {
        tableRef.current?.scrollTo?.({ top: 0 });
      } catch {
        /* ignore */
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [page, pageSize, useVirtualTable, loading]);

  const resetFilters = () => {
    setSearch('');
    setTherapyFilter(undefined);
    setAddedByFilter(undefined);
    setStatusFilter('active');
    setLeadPotentialFilter(undefined);
    setLeadSourceFilter(undefined);
    setCountryFilter(undefined);
    setCityFilter(undefined);
    setRoleFilter(undefined);
    setDomainFilter(undefined);
    setTagsFilter(undefined);
    setDoNotSendFilter(undefined);
    setIsSuppressedFilter(undefined);
    setLastContactedDaysFilter(undefined);
    setMinEmailsSentFilter(undefined);
    setMaxEmailsSentFilter(undefined);
    setExcludeLeadSource(undefined);
    setExcludeLeadPotential(undefined);
    setExcludeAddedBy(undefined);
    setExcludeCountry(undefined);
    setExcludeCity(undefined);
    setExcludeRole(undefined);
    setExcludeDomain(undefined);
    setExcludeTags(undefined);
    setExcludeTherapy(undefined);
    setPage(1);
  };

  const handleExportExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      // Fetch ALL leads matching current filters (in batches)
      const allLeads: Lead[] = [];
      let currentPage = 1;
      const batchSize = 100;
      let hasMore = true;
      let totalCount = 0;

      while (hasMore) {
        const res = await outreachLeadsApi.list({
          page: currentPage,
          page_size: batchSize,
          search: search || undefined,
          do_not_send: doNotSendFilter,
          is_suppressed: isSuppressedFilter,
          country: countryFilter,
          city: cityFilter,
          therapy_interest: therapyFilter,
          lead_source: leadSourceFilter,
          lead_potential: leadPotentialFilter,
          added_by: addedByFilter,
          role: roleFilter,
          domain: domainFilter,
          tags: tagsFilter,
          status: statusFilter,
          last_contacted_days_ago: lastContactedDaysFilter,
          min_emails_sent: minEmailsSentFilter,
          max_emails_sent: maxEmailsSentFilter,
          exclude_lead_source: excludeLeadSource,
          exclude_lead_potential: excludeLeadPotential,
          exclude_added_by: excludeAddedBy,
          exclude_country: excludeCountry,
          exclude_city: excludeCity,
          exclude_role: excludeRole,
          exclude_domain: excludeDomain,
          exclude_tags: excludeTags,
          exclude_therapy_interest: excludeTherapy,
        });
        allLeads.push(...res.items);
        totalCount = res.total;
        if (res.items.length < batchSize || allLeads.length >= totalCount) {
          hasMore = false;
        } else {
          currentPage++;
        }
      }

      if (allLeads.length === 0) {
        message.warning('No leads to export');
        return;
      }

      // Map leads to flat rows for Excel
      const rows = allLeads.map(lead => ({
        'Email': lead.email || '',
        'First Name': lead.first_name || '',
        'Last Name': lead.last_name || '',
        'Company': lead.company || '',
        'Role': lead.role || '',
        'Domain': lead.domain || '',
        'Country': lead.country || '',
        'City': lead.city || '',
        'Therapy Interest': (lead.therapy_interest || []).join(', '),
        'Lead Source': lead.lead_source || '',
        'Lead Potential': lead.lead_potential || '',
        'Added By': lead.added_by || '',
        'Tags': (lead.tags || []).join(', '),
        'Last Contacted': lead.last_contacted_at ? dayjs(lead.last_contacted_at).format('DD-MM-YYYY') : '',
        'Total Emails Sent': lead.total_emails_sent || 0,
        'Next Follow-up': lead.next_followup_at ? dayjs(lead.next_followup_at).format('DD-MM-YYYY') : '',
        'Status': getLeadStatusLabel(lead),
        'Notes': lead.notes || '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Leads');

      // Auto-size columns
      const colWidths = Object.keys(rows[0]).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String((r as any)[key]).length).slice(0, 100)) + 2,
      }));
      ws['!cols'] = colWidths;

      const timestamp = dayjs().format('YYYY-MM-DD_HH-mm');
      XLSX.writeFile(wb, `leads_export_${timestamp}.xlsx`);
      message.success(`Exported ${allLeads.length} lead${allLeads.length !== 1 ? 's' : ''} to Excel`);
    } catch {
      message.error('Failed to export leads');
    } finally {
      setExportLoading(false);
    }
  }, [search, doNotSendFilter, isSuppressedFilter, countryFilter, cityFilter,
    therapyFilter, leadSourceFilter, leadPotentialFilter, addedByFilter, roleFilter,
    domainFilter, tagsFilter, statusFilter, lastContactedDaysFilter,
    minEmailsSentFilter, maxEmailsSentFilter, excludeLeadSource, excludeLeadPotential,
    excludeAddedBy, excludeCountry, excludeCity, excludeRole, excludeDomain,
    excludeTags, excludeTherapy]);

  const handleEdit = useCallback((lead: Lead) => {
    setEditingLead(lead);
    editForm.setFieldsValue({
      status: leadToStatusFilter(lead),
      notes: lead.notes,
      tags: lead.tags || [],
      first_name: lead.first_name,
      last_name: lead.last_name,
      company: lead.company,
      role: lead.role,
      domain: lead.domain,
      country: lead.country,
      city: lead.city,
      therapy_interest: lead.therapy_interest || [],
      lead_source: lead.lead_source,
      lead_potential: lead.lead_potential,
      next_followup_at: lead.next_followup_at ? dayjs(lead.next_followup_at) : null,
    });
    setEditDrawerVisible(true);
  }, [editForm]);

  const handleUpdateLead = async (values: any) => {
    if (!editingLead) return;
    try {
      const update: LeadUpdate = {
        ...statusToLeadUpdate(values.status as LeadStatusFilter),
        notes: values.notes,
        tags: values.tags,
        first_name: values.first_name,
        last_name: values.last_name,
        company: values.company,
        role: values.role,
        domain: values.domain,
        country: values.country,
        city: values.city,
        therapy_interest: values.therapy_interest,
        lead_source: values.lead_source,
        lead_potential: values.lead_potential,
        // Send null explicitly when cleared (not undefined) so backend knows to clear the field
        next_followup_at: values.next_followup_at ? values.next_followup_at.toISOString() : null,
      };
      await outreachLeadsApi.update(editingLead.id, update);
      message.success('Lead updated'); setEditDrawerVisible(false); fetchLeads();
    } catch { message.error('Failed to update lead'); }
  };

  const handleFileUpload = async (file: File) => {
    setImportFile(file);
    setUploadLoading(true);
    try {
      const inspection = await outreachLeadsApi.inspectImportFile(file);
      setColumnInspection(inspection); setImportStep(1);
    } catch (e: any) { message.error(e.response?.data?.detail || 'Failed to inspect file'); }
    finally { setUploadLoading(false); }
    return false;
  };

  const handleColumnMapping = async (values: Record<string, string>) => {
    const filtered: Record<string, string> = {};
    for (const [col, field] of Object.entries(values)) {
      if (field && field !== '') {
        filtered[col] = field;
      }
    }
    
    // Validate that email field is mapped exactly once
    const emailMappings = Object.values(filtered).filter(f => f === 'email');
    if (emailMappings.length === 0) {
      message.error('Please map exactly one column to the Email field (required)');
      return;
    }
    if (emailMappings.length > 1) {
      message.error('Only one column can be mapped to the Email field');
      return;
    }

    // Check for duplicate field assignments (should not happen with disabled options, but safety check)
    const fieldCounts: Record<string, number> = {};
    for (const field of Object.values(filtered)) {
      fieldCounts[field] = (fieldCounts[field] || 0) + 1;
    }
    const duplicateFields = Object.entries(fieldCounts).filter(([_, c]) => c > 1).map(([f]) => f);
    if (duplicateFields.length > 0) {
      message.error(`Each field can only be mapped once. Duplicates found: ${duplicateFields.join(', ')}`);
      return;
    }
    
    setColumnMapping(filtered);
    setImportStep(2);
    setInvalidEmails([]); // Reset
    setCorrectedDbDuplicates(new Set()); // Reset corrected duplicates

    if (importFile) {
      setLoadingPreview(true);
      try {
        const preview = await outreachLeadsApi.previewImport(importFile, filtered);
        setPreviewData(preview);

        // Validate emails in preview rows
        const emailCol = Object.entries(filtered).find(([_, f]) => f === 'email')?.[0];
        if (emailCol && preview.preview_rows) {
          const invalids: { row: number; original: string; corrected?: string }[] = [];
          preview.preview_rows.forEach((row: Record<string, any>, idx: number) => {
            const emailVal = row.email;
            if (emailVal && !isValidEmail(emailVal)) {
              invalids.push({ row: idx, original: emailVal });
            }
          });
          setInvalidEmails(invalids);
        }
      } catch (e: any) {
        message.error(e.response?.data?.detail || 'Failed to load preview');
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  const handleCommitImport = async () => {
    if (!importFile) return;
    
    // Validate metadata form (Added By is required)
    try {
      await metadataForm.validateFields(['added_by']);
    } catch {
      message.error('Please select "Added By" in Import Metadata before importing');
      return;
    }

    setImportLoading(true);
    try {
      // Collect import metadata from form
      const metaValues = metadataForm.getFieldsValue();
      const importMeta: ImportMetadata = {};
      if (metaValues.therapy_interest?.length) importMeta.therapy_interest = metaValues.therapy_interest;
      if (metaValues.lead_source) importMeta.lead_source = metaValues.lead_source;
      if (metaValues.added_by) importMeta.added_by = metaValues.added_by;
      if (metaValues.tags?.length) importMeta.tags = metaValues.tags;
      if (metaValues.country) importMeta.country = metaValues.country;
      if (metaValues.city) importMeta.city = metaValues.city;
      if (metaValues.lead_potential) importMeta.lead_potential = metaValues.lead_potential;

      const hasMetadata = Object.keys(importMeta).length > 0;
      const result = await outreachLeadsApi.commitImport(importFile, columnMapping, hasMetadata ? importMeta : undefined);

      const parts = [`${result.imported_count} imported`];
      if (result.duplicate_skipped_count > 0) parts.push(`${result.duplicate_skipped_count} duplicates skipped`);
      if (result.skipped_count > 0) parts.push(`${result.skipped_count} errors`);
      message.success(`Import completed: ${parts.join(', ')}`);
      resetImportState();
      fetchLeads();
    } catch (e: any) { message.error(e.response?.data?.detail || 'Failed to import leads'); }
    finally { setImportLoading(false); }
  };

  const resetImportState = () => {
    setImportModalVisible(false); setImportStep(0); setImportFile(null);
    setColumnInspection(null); setColumnMapping({}); setPreviewData(null);
    setMappedFields({});
    setInvalidEmails([]);
    setCorrectedDbDuplicates(new Set());
    setAddedBySet(false);
    metadataForm.resetFields();
    manualForm.resetFields();
    mappingForm.resetFields();
    setImportMode('file');
  };

  // Debounced check of corrected emails against the DB
  const checkCorrectedEmailsInDb = useCallback((updatedInvalids: typeof invalidEmails) => {
    if (emailCheckTimerRef.current) clearTimeout(emailCheckTimerRef.current);
    emailCheckTimerRef.current = setTimeout(async () => {
      const emailsToCheck = updatedInvalids
        .filter(e => e.corrected && isValidEmail(e.corrected))
        .map(e => e.corrected!.trim().toLowerCase());
      if (emailsToCheck.length === 0) {
        setCorrectedDbDuplicates(new Set());
        return;
      }
      try {
        const result = await outreachLeadsApi.checkEmails(emailsToCheck);
        setCorrectedDbDuplicates(new Set(result.existing.map(e => e.toLowerCase())));
      } catch {
        // Silently fail – the preview duplicate info from the original scan still works
      }
    }, 400);
  }, []);

  const handleManualLeadSubmit = async (values: any) => {
    setManualLoading(true);
    try {
      const leadData: LeadCreate = {
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        company: values.company,
        role: values.role,
        domain: values.domain,
        country: values.country,
        city: values.city,
        therapy_interest: values.therapy_interest,
        lead_source: values.lead_source || 'manual',
        added_by: values.added_by,
        lead_potential: values.lead_potential,
        tags: values.tags,
        notes: values.notes,
      };
      await outreachLeadsApi.create(leadData);
      message.success('Lead added successfully');
      resetImportState();
      fetchLeads();
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || 'Failed to add lead';
      message.error(errorMsg);
    } finally {
      setManualLoading(false);
    }
  };

  const statusToLeadUpdate = (status: LeadStatusFilter): LeadUpdate => {
    if (status === 'active') {
      return { bounce_status: 'not_bounced', unsubscribe_status: 'subscribed' };
    }
    if (status === 'bounces') {
      return { bounce_status: 'bounced' };
    }
    return { bounce_status: 'not_bounced', unsubscribe_status: 'unsubscribed' };
  };

  // ── Bulk edit handler ──
  const handleBulkEdit = async () => {
    if (!bulkEditField) { message.warning('Please select a field to update'); return; }
    try {
      const values = bulkEditForm.getFieldsValue();
      const fieldValue = values[bulkEditField];
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        message.warning('Please set a value for the selected field');
        return;
      }
      setBulkEditLoading(true);
      let update: LeadUpdate;
      if (bulkEditField === 'status') {
        update = statusToLeadUpdate(fieldValue as LeadStatusFilter);
      } else {
        let processedValue = fieldValue;
        if (bulkEditField === 'next_followup_at' && fieldValue) {
          processedValue = fieldValue.toISOString();
        }
        update = { [bulkEditField]: processedValue };
      }
      const result = await outreachLeadsApi.bulkUpdate(
        selectedRowKeys.map(k => String(k)),
        update,
      );
      message.success(`Updated ${result.updated_count} lead${result.updated_count !== 1 ? 's' : ''}`);
      setBulkEditModalVisible(false);
      setBulkEditField(undefined);
      bulkEditForm.resetFields();
      setSelectedRowKeys([]);
      fetchLeads();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to bulk update leads');
    } finally {
      setBulkEditLoading(false);
    }
  };

  // ── Sticky horizontal scrollbar sync (non-virtual path) ──
  useEffect(() => {
    if (useVirtualTable) return;
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    const tableBody = (
      wrapper.querySelector('.ant-table-body') ||
      wrapper.querySelector('.ant-table-content')
    ) as HTMLDivElement | null;
    const fakeScroll = fakeScrollRef.current;
    const fakeInner = fakeScrollInnerRef.current;
    if (!tableBody || !fakeScroll || !fakeInner) return;

    const syncWidth = () => {
      const scrollWidth = tableBody.scrollWidth;
      const clientWidth = tableBody.clientWidth;
      const overflowing = scrollWidth > clientWidth;
      fakeInner.style.width = `${scrollWidth}px`;
      setIsTableOverflowing(overflowing);
    };
    const initialTimer = setTimeout(syncWidth, 50);

    const onTableScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      fakeScroll.scrollLeft = tableBody.scrollLeft;
      isSyncingScroll.current = false;
    };
    const onFakeScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      tableBody.scrollLeft = fakeScroll.scrollLeft;
      isSyncingScroll.current = false;
    };

    tableBody.addEventListener('scroll', onTableScroll);
    fakeScroll.addEventListener('scroll', onFakeScroll);

    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(tableBody);

    return () => {
      clearTimeout(initialTimer);
      tableBody.removeEventListener('scroll', onTableScroll);
      fakeScroll.removeEventListener('scroll', onFakeScroll);
      resizeObserver.disconnect();
    };
  }, [leads, columnWidths, useVirtualTable]);

  // ── Sticky horizontal scrollbar sync (virtual path) ──
  useEffect(() => {
    if (!useVirtualTable) return;
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;
    const fakeScroll = fakeScrollRef.current;
    const fakeInner = fakeScrollInnerRef.current;
    if (!fakeScroll || !fakeInner) return;

    let holder: HTMLDivElement | null = null;
    let holderInner: HTMLDivElement | null = null;
    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const readOffsetLeftFromInner = (el: HTMLDivElement): number => {
      const margin = parseFloat(el.style.marginLeft || '0') || 0;
      return -margin;
    };

    const computeScrollWidth = () => Object.values(columnWidths).reduce((a, b) => a + b, 0);

    const syncWidth = () => {
      if (!holder) return;
      const scrollWidth = computeScrollWidth();
      const clientWidth = holder.clientWidth;
      const overflowing = scrollWidth > clientWidth;
      fakeInner.style.width = `${scrollWidth}px`;
      setIsTableOverflowing(overflowing);
    };

    const applyHorizontalScroll = (scrollLeft: number) => {
      try {
        tableRef.current?.scrollTo?.({ left: scrollLeft });
      } catch {
        /* ignore */
      }
      // scrollTo({ left }) updates virtual rows but skips rc-table header sync
      syncTableHeaderScrollLeft(wrapper, scrollLeft);
    };

    const onFakeScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      applyHorizontalScroll(fakeScroll.scrollLeft);
      isSyncingScroll.current = false;
    };

    const onInnerStyleChange = () => {
      if (!holderInner) return;
      if (isSyncingScroll.current) return;
      const offset = readOffsetLeftFromInner(holderInner);
      isSyncingScroll.current = true;
      fakeScroll.scrollLeft = offset;
      syncTableHeaderScrollLeft(wrapper, offset);
      isSyncingScroll.current = false;
    };

    const tryAttach = () => {
      holder = wrapper.querySelector('.ant-table-tbody-virtual-holder') as HTMLDivElement | null;
      holderInner = wrapper.querySelector('.ant-table-tbody-virtual-holder-inner') as HTMLDivElement | null;
      if (!holder || !holderInner) return false;

      syncWidth();

      resizeObserver = new ResizeObserver(syncWidth);
      resizeObserver.observe(holder);

      mutationObserver = new MutationObserver(onInnerStyleChange);
      mutationObserver.observe(holderInner, { attributes: true, attributeFilter: ['style'] });
      return true;
    };

    fakeScroll.addEventListener('scroll', onFakeScroll);
    let attachTimer: ReturnType<typeof setTimeout> | null = null;
    const attachWithRetry = (attempts = 0) => {
      if (tryAttach()) return;
      if (attempts >= 10) return;
      attachTimer = setTimeout(() => attachWithRetry(attempts + 1), 60);
    };
    attachWithRetry();

    return () => {
      if (attachTimer) clearTimeout(attachTimer);
      fakeScroll.removeEventListener('scroll', onFakeScroll);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [leads, columnWidths, useVirtualTable]);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (newKeys: React.Key[]) => setSelectedRowKeys(newKeys),
    columnWidth: 48,
    preserveSelectedRowKeys: true,
  }), [selectedRowKeys]);

  const BULK_EDIT_FIELDS = [
    { value: 'therapy_interest', label: 'Therapy Interest', type: 'multi-select' },
    { value: 'lead_potential', label: 'Lead Potential', type: 'select' },
    { value: 'lead_source', label: 'Lead Source', type: 'select' },
    { value: 'added_by', label: 'Added By', type: 'select' },
    { value: 'country', label: 'Country', type: 'input' },
    { value: 'city', label: 'City', type: 'input' },
    { value: 'company', label: 'Company', type: 'input' },
    { value: 'domain', label: 'Domain', type: 'input' },
    { value: 'tags', label: 'Tags', type: 'tags' },
    { value: 'status', label: 'Status', type: 'select' },
    { value: 'next_followup_at', label: 'Next Follow-up', type: 'date' },
  ] as const;

  const handleColumnResizeStart = useCallback((columnKey: LeadColumnKey, event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = columnWidths[columnKey];
    let lastWidth = startWidth;
    let frameId: number | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(80, startWidth + delta);

      if (nextWidth !== lastWidth) {
        lastWidth = nextWidth;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          setColumnWidths(prev => ({ ...prev, [columnKey]: nextWidth }));
        });
      }
    };

    const handleMouseUp = () => {
      if (frameId) cancelAnimationFrame(frameId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths]);

  const getResizableHeaderProps = useCallback((columnKey: LeadColumnKey): ResizableHeaderCellProps => ({
    width: columnWidths[columnKey],
    onResizeStart: (event: React.MouseEvent<HTMLSpanElement>) => handleColumnResizeStart(columnKey, event),
  }), [columnWidths, handleColumnResizeStart]);

  const columns: ColumnsType<Lead> = useMemo(() => {
    const compact = useCompactCells;
    return [
      {
        title: 'Actions',
        key: 'actions',
        width: columnWidths.actions,
        onHeaderCell: () => getResizableHeaderProps('actions'),
        render: (_, r) => (
          <Button
            type="link"
            size={compact ? 'small' : 'middle'}
            icon={<EditOutlined />}
            onClick={() => handleEdit(r)}
          >
            {compact ? null : 'Edit'}
          </Button>
        ),
      },
      {
        title: 'Name',
        key: 'name',
        width: columnWidths.name,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('name'),
        render: (_, r) => {
          const fullName = [r.first_name, r.last_name].filter(Boolean).join(' ') || '-';
          return renderEllipsisCell(fullName, compact);
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: columnWidths.email,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('email'),
        render: (value: string) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Company',
        dataIndex: 'company',
        key: 'company',
        width: columnWidths.company,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('company'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        width: columnWidths.role,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('role'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Therapy Interest',
        dataIndex: 'therapy_interest',
        key: 'therapy_interest',
        width: columnWidths.therapy_interest,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('therapy_interest'),
        render: (values: string[] | null) => compact
          ? renderJoinedListCell(values)
          : (!values?.length ? '-' : (
            <div style={{ ...cellEllipsisStyle, whiteSpace: 'nowrap' }}>
              <Space size={4}>{values.map(v => <Tag key={v} color="geekblue">{v}</Tag>)}</Space>
            </div>
          )),
      },
      {
        title: 'Last Contacted',
        dataIndex: 'last_contacted_at',
        key: 'last_contacted_at',
        width: columnWidths.last_contacted_at,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('last_contacted_at'),
        render: (value: string | null) => {
          if (!value) return '-';
          const d = dayjs(value);
          if (compact) return renderEllipsisCell(d.format('DD-MM-YYYY'), true);
          const diff = dayjs().startOf('day').diff(d.startOf('day'), 'day');
          const diffText = diff === 0 ? 'Today' : `${diff}d ago`;
          return (
            <span>
              {d.format('DD-MM-YYYY')}
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                ({diffText})
              </Text>
            </span>
          );
        },
      },
      {
        title: 'Domain',
        dataIndex: 'domain',
        key: 'domain',
        width: columnWidths.domain,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('domain'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country',
        width: columnWidths.country,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('country'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city',
        width: columnWidths.city,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('city'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Source',
        dataIndex: 'lead_source',
        key: 'lead_source',
        width: columnWidths.lead_source,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('lead_source'),
        render: (value: string | null) => compact
          ? renderEllipsisCell(value || '-', true)
          : (value ? <Tag>{value}</Tag> : '-'),
      },
      {
        title: 'Potential',
        dataIndex: 'lead_potential',
        key: 'lead_potential',
        width: columnWidths.lead_potential,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('lead_potential'),
        render: (value: string | null) => compact
          ? renderEllipsisCell(value || '-', true)
          : (value ? <Tag color={LEAD_POTENTIAL_COLORS[value] || 'default'}>{value}</Tag> : '-'),
      },
      {
        title: 'Added By',
        dataIndex: 'added_by',
        key: 'added_by',
        width: columnWidths.added_by,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('added_by'),
        render: (value: string | null) => renderEllipsisCell(value || '-', compact),
      },
      {
        title: 'Emails Sent',
        dataIndex: 'total_emails_sent',
        key: 'total_emails_sent',
        width: columnWidths.total_emails_sent,
        onHeaderCell: () => getResizableHeaderProps('total_emails_sent'),
        render: (value: number) => value || 0,
      },
      {
        title: 'Next Follow-up',
        dataIndex: 'next_followup_at',
        key: 'next_followup_at',
        width: columnWidths.next_followup_at,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('next_followup_at'),
        render: (value: string | null) => {
          if (!value) return '-';
          const d = dayjs(value);
          const text = d.format('YYYY-MM-DD');
          if (compact) return renderEllipsisCell(text, true);
          const isPast = d.isBefore(dayjs());
          return <Text type={isPast ? 'danger' : undefined}>{text}</Text>;
        },
      },
      {
        title: 'Tags',
        dataIndex: 'tags',
        key: 'tags',
        width: columnWidths.tags,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('tags'),
        render: (tags: string[] | null) => compact
          ? renderJoinedListCell(tags)
          : (!tags?.length ? '-' : (
            <div style={{ ...cellEllipsisStyle, whiteSpace: 'nowrap' }}>
              <Space size={4}>{tags.map(tag => <Tag key={tag}>{tag}</Tag>)}</Space>
            </div>
          )),
      },
      {
        title: 'Status',
        key: 'status',
        width: columnWidths.status,
        ellipsis: true,
        onHeaderCell: () => getResizableHeaderProps('status'),
        render: (_, r) => compact
          ? renderEllipsisCell(getLeadStatusLabel(r), true)
          : renderLeadStatusTag(r),
      },
    ];
  }, [columnWidths, getResizableHeaderProps, handleEdit, useCompactCells]);

  const tableScrollX = useMemo(
    () => Object.values(columnWidths).reduce((totalWidth, width) => totalWidth + width, 0),
    [columnWidths],
  );

  const renderColumnSamples = (col: string) => {
    if (!columnInspection?.column_samples) return null;
    const samples = columnInspection.column_samples[col] || [];
    const nonNull = samples.filter((s): s is string => s !== null && s !== undefined);
    if (nonNull.length === 0) return <Text type="secondary" style={{ fontSize: 11 }}>No data</Text>;

    return (
      <div style={{
        marginTop: 4,
        padding: '6px 10px',
        background: isDark ? '#1f1f1f' : '#fafafa',
        border: isDark ? '1px solid #434343' : '1px solid #f0f0f0',
        borderRadius: 6,
        maxHeight: 120,
        overflowY: 'auto',
      }}>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
          <InfoCircleOutlined style={{ marginRight: 4 }} />
          Sample values:
        </Text>
        {nonNull.map((val, idx) => (
          <div key={idx} style={{
            fontSize: 12,
            padding: '2px 0',
            borderBottom: idx < nonNull.length - 1 ? (isDark ? '1px solid #434343' : '1px solid #f5f5f5') : 'none',
            color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 350,
          }}>
            <Tooltip title={val}>
              {val}
            </Tooltip>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>Leads</span>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={handleExportExcel} loading={exportLoading}>Export Leads</Button>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>Import Leads</Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <Space wrap style={{ width: '100%' }} size="middle">
          <Input placeholder="Search by email, name, or company" prefix={<SearchOutlined />}
            value={search} onChange={e => setSearch(e.target.value)} style={{ width: 250 }} />
          
          <Select
            mode="multiple" placeholder="Therapy Interest" style={{ minWidth: 180 }} allowClear
            value={therapyFilter} onChange={(v) => setTherapyFilter(v?.length ? v : undefined)}
            maxTagCount="responsive"
            loading={filterOptionsLoading}
          >
            {filterOptions.therapy_interest.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
          </Select>

          <Select placeholder="Status" style={{ width: 140 }} allowClear value={statusFilter} onChange={setStatusFilter}>
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="bounces">Bounces</Select.Option>
            <Select.Option value="unsubscribes">Unsubscribes</Select.Option>
          </Select>

          <Select
            mode="multiple" placeholder="Potential" style={{ minWidth: 140 }} allowClear
            value={leadPotentialFilter} onChange={(v) => setLeadPotentialFilter(v?.length ? v : undefined)}
            maxTagCount="responsive"
            loading={filterOptionsLoading}
          >
            {filterOptions.lead_potential.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
          </Select>

          <Select
            mode="multiple" placeholder="Added By" style={{ minWidth: 140 }} allowClear
            value={addedByFilter} onChange={(v) => setAddedByFilter(v?.length ? v : undefined)}
            maxTagCount="responsive"
            loading={filterOptionsLoading}
          >
            {filterOptions.added_by.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
          </Select>

          <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerVisible(true)}>
            More Filters
          </Button>

          <Button icon={<ReloadOutlined />} onClick={resetFilters} type="link">
            Reset Filters
          </Button>
        </Space>
      </Card>

      {/* Additional Filters Drawer */}
      <Drawer
        title="More Filters"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={400}
      >
        <Form layout="vertical">
          <Form.Item label="Source">
            <Select mode="multiple" placeholder="Select Source(s)" allowClear
              value={leadSourceFilter} onChange={(v) => setLeadSourceFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              loading={filterOptionsLoading}
            >
              {filterOptions.lead_source.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item label="Role">
            <Select 
              mode="tags" 
              placeholder="Type role(s) to filter" 
              allowClear
              value={roleFilter} 
              onChange={(v) => setRoleFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching roles'}
            >
              {filterOptions.roles.map(role => (
                <Select.Option key={role} value={role}>{role}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Domain">
            <Select 
              mode="tags" 
              placeholder="Type domain(s) to filter" 
              allowClear
              value={domainFilter} 
              onChange={(v) => setDomainFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching domains'}
            >
              {filterOptions.domains.map(domain => (
                <Select.Option key={domain} value={domain}>{domain}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Country">
            <Select 
              mode="tags" 
              placeholder="Type country(ies) to filter" 
              allowClear
              value={countryFilter} 
              onChange={(v) => setCountryFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching countries'}
            >
              {filterOptions.countries.map(country => (
                <Select.Option key={country} value={country}>{country}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="City">
            <Select 
              mode="tags" 
              placeholder="Type city(ies) to filter" 
              allowClear
              value={cityFilter} 
              onChange={(v) => setCityFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching cities'}
            >
              {filterOptions.cities.map(city => (
                <Select.Option key={city} value={city}>{city}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Tags">
            <Select 
              mode="tags" 
              placeholder="Type tag(s) to filter" 
              allowClear
              value={tagsFilter} 
              onChange={(v) => setTagsFilter(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
            >
              {filterOptions.tags.map(tag => (
                <Select.Option key={tag} value={tag}>{tag}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Last Contacted">
            <Select
              placeholder="Any time"
              style={{ width: '100%' }}
              allowClear
              value={lastContactedDaysFilter}
              onChange={setLastContactedDaysFilter}
            >
              {LAST_CONTACTED_OPTIONS.map(o => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Emails Sent Range">
            <Space>
              <InputNumber
                placeholder="Min"
                min={0}
                value={minEmailsSentFilter}
                onChange={(v) => setMinEmailsSentFilter(v ?? undefined)}
                style={{ width: '100%' }}
              />
              <Text type="secondary">to</Text>
              <InputNumber
                placeholder="Max"
                min={0}
                value={maxEmailsSentFilter}
                onChange={(v) => setMaxEmailsSentFilter(v ?? undefined)}
                style={{ width: '100%' }}
              />
            </Space>
          </Form.Item>

          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
              <ExclamationCircleOutlined style={{ marginRight: 6, color: '#faad14' }} />
              Exclude Filters
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
              Leads matching these values will be excluded from results.
            </Text>

            <Form.Item label="Exclude Therapy Interest">
              <Select mode="multiple" placeholder="Exclude therapy areas" allowClear
                value={excludeTherapy} onChange={(v) => setExcludeTherapy(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.therapy_interest.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Source">
              <Select mode="multiple" placeholder="Exclude source(s)" allowClear
                value={excludeLeadSource} onChange={(v) => setExcludeLeadSource(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.lead_source.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Potential">
              <Select mode="multiple" placeholder="Exclude potential(s)" allowClear
                value={excludeLeadPotential} onChange={(v) => setExcludeLeadPotential(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.lead_potential.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Added By">
              <Select mode="multiple" placeholder="Exclude person(s)" allowClear
                value={excludeAddedBy} onChange={(v) => setExcludeAddedBy(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.added_by.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Country">
              <Select 
                mode="tags" 
                placeholder="Exclude country(ies)" 
                allowClear
                value={excludeCountry} 
                onChange={(v) => setExcludeCountry(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching countries'}
              >
                {filterOptions.countries.map(country => (
                  <Select.Option key={country} value={country}>{country}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude City">
              <Select 
                mode="tags" 
                placeholder="Exclude city(ies)" 
                allowClear
                value={excludeCity} 
                onChange={(v) => setExcludeCity(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching cities'}
              >
                {filterOptions.cities.map(city => (
                  <Select.Option key={city} value={city}>{city}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Role">
              <Select 
                mode="tags" 
                placeholder="Exclude role(s)" 
                allowClear
                value={excludeRole} 
                onChange={(v) => setExcludeRole(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching roles'}
              >
                {filterOptions.roles.map(role => (
                  <Select.Option key={role} value={role}>{role}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Domain">
              <Select 
                mode="tags" 
                placeholder="Exclude domain(s)" 
                allowClear
                value={excludeDomain} 
                onChange={(v) => setExcludeDomain(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching domains'}
              >
                {filterOptions.domains.map(domain => (
                  <Select.Option key={domain} value={domain}>{domain}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Tags">
              <Select 
                mode="tags" 
                placeholder="Exclude tag(s)" 
                allowClear
                value={excludeTags} 
                onChange={(v) => setExcludeTags(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
              >
                {filterOptions.tags.map(tag => (
                  <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Button type="link" onClick={resetFilters} style={{ padding: 0, marginTop: 8 }}>
            Clear All Filters
          </Button>
        </Form>
      </Drawer>

      {/* Bulk Edit Bar */}
      {selectedRowKeys.length > 0 && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 1005,
          marginBottom: 12,
          padding: '10px 16px',
          background: isDark ? '#111a2c' : '#e6f4ff',
          border: isDark ? '1px solid #1d39c4' : '1px solid #91caff',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <Space>
            <Checkbox
              checked={leads.length > 0 && leads.every(l => selectedRowKeys.includes(l.id))}
              indeterminate={leads.some(l => selectedRowKeys.includes(l.id)) && !leads.every(l => selectedRowKeys.includes(l.id))}
              onChange={e => {
                const currentPageKeys = leads.map(l => l.id);
                if (e.target.checked) {
                  // Add current page leads to existing selection
                  const nextKeys = Array.from(new Set([...selectedRowKeys, ...currentPageKeys]));
                  setSelectedRowKeys(nextKeys);
                } else {
                  // Remove ONLY current page leads from selection
                  const nextKeys = selectedRowKeys.filter(k => !currentPageKeys.includes(k as string));
                  setSelectedRowKeys(nextKeys);
                }
              }}
            />
            <Text strong>{selectedRowKeys.length} lead{selectedRowKeys.length !== 1 ? 's' : ''} selected</Text>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => { setBulkEditModalVisible(true); setBulkEditField(undefined); bulkEditForm.resetFields(); }}
            >
              Bulk Edit
            </Button>
            <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
          </Space>
        </div>
      )}

      {/* Table */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <style>{`
          .column-resize-handle:hover .handle-bar {
            background: #1890ff !important;
            width: 3px !important;
          }
          .column-resize-handle:hover {
            background: rgba(24, 144, 255, 0.1) !important;
          }
          .column-resize-handle.is-resizing .handle-bar {
            background: #1890ff !important;
            width: 3px !important;
          }
          .column-resize-handle.is-resizing {
            background: rgba(24, 144, 255, 0.15) !important;
          }
          .leads-data-table .ant-table-thead > tr > th:not(.ant-table-selection-column) {
            position: relative;
            overflow: hidden;
          }
          .leads-data-table .ant-table-thead > tr > th:not(.ant-table-selection-column)::after {
            content: "";
            position: absolute;
            right: 0;
            top: 25%;
            height: 50%;
            width: 1px;
            background-color: #f0f0f0;
          }
          .leads-data-table .ant-table-thead .ant-table-selection-column {
            overflow: visible;
          }
          .leads-data-table .ant-table-thead .column-resize-handle {
            right: 0;
          }
          /* Remove Ant Design's fixed column shadow/border */
          .ant-table-cell-fix-left-last::after,
          .ant-table-cell-fix-right-first::after {
            box-shadow: none !important;
          }
          /* Hide the original table horizontal scrollbar */
          .ant-table-body::-webkit-scrollbar,
          .ant-table-content::-webkit-scrollbar,
          .ant-table-header::-webkit-scrollbar {
            height: 0px !important;
            display: none !important;
          }
          .ant-table-body,
          .ant-table-content,
          .ant-table-header {
            scrollbar-width: none !important;  /* Firefox */
            -ms-overflow-style: none !important; /* IE/Edge */
          }
          .leads-sticky-scrollbar-top {
            position: sticky;
            top: 0;
            z-index: 1001; /* Must be above sticky table header */
            overflow-x: auto;
            overflow-y: hidden;
            height: 12px;
            background: ${isDark ? '#1f1f1f' : '#fafafa'};
            border-bottom: 1px solid ${isDark ? '#434343' : '#f0f0f0'};
            display: ${isTableOverflowing ? 'block' : 'none'};
          }
          .leads-sticky-scrollbar-top::-webkit-scrollbar {
            height: 8px;
          }
          .leads-sticky-scrollbar-top::-webkit-scrollbar-thumb {
            background: ${isDark ? '#555' : '#c1c1c1'};
            border-radius: 4px;
            border: 1px solid ${isDark ? '#1f1f1f' : '#fafafa'};
          }
          .leads-sticky-scrollbar-top::-webkit-scrollbar-thumb:hover {
            background: ${isDark ? '#777' : '#999'};
          }
          .leads-sticky-scrollbar-top::-webkit-scrollbar-track {
            background: ${isDark ? '#1f1f1f' : '#fafafa'};
          }
          /* Hide Ant Design's built-in sticky scrollbar */
          .ant-table-sticky-scrollbar {
            display: none !important;
          }
          /* Hide rc-virtual-list's built-in horizontal scrollbar in virtual mode */
          .leads-data-table .ant-table-tbody-virtual-scrollbar-horizontal {
            display: none !important;
          }
        `}</style>
        {/* Top pagination */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={LEADS_PAGE_SIZE_OPTIONS}
            showTotal={t => `Total ${t} leads`}
            onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
          />
        </div>
        {/* Sticky horizontal scrollbar at TOP of table */}
        <div
          ref={fakeScrollRef}
          className="leads-sticky-scrollbar-top"
        >
          <div ref={fakeScrollInnerRef} style={{ height: 1 }} />
        </div>
        <div ref={tableWrapperRef} className="leads-data-table">
          <Table
            ref={tableRef}
            virtual={useVirtualTable}
            size={useCompactCells ? 'small' : 'middle'}
            tableLayout="fixed"
            components={{ header: { cell: ResizableTableHeaderCell } }}
            columns={columns}
            dataSource={leads}
            loading={loading}
            rowKey="id"
            rowSelection={rowSelection}
            pagination={{ 
              position: ['bottomRight'],
              current: page, pageSize, total, showSizeChanger: true,
              pageSizeOptions: LEADS_PAGE_SIZE_OPTIONS,
              showTotal: t => `Total ${t} leads`, onChange: (p, ps) => { setPage(p); setPageSize(ps); } 
            }}
            scroll={useVirtualTable ? { x: tableScrollX, y: tableScrollY } : { x: tableScrollX }}
            sticky={useVirtualTable ? undefined : {
              offsetHeader: isTableOverflowing ? 12 : 0,
            }}
          />
        </div>
      </Card>

      {/* Edit Drawer */}
      <Drawer title="Edit Lead" placement="right" onClose={() => setEditDrawerVisible(false)} open={editDrawerVisible} width={520}>
        {editingLead && (
          <Form form={editForm} layout="vertical" onFinish={handleUpdateLead}>
            <Form.Item label="Email"><Input value={editingLead.email} disabled /></Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="first_name" label="First Name"><Input /></Form.Item>
              <Form.Item name="last_name" label="Last Name"><Input /></Form.Item>
              <Form.Item name="company" label="Company"><Input /></Form.Item>
              <Form.Item name="role" label="Role"><Input /></Form.Item>
              <Form.Item name="domain" label="Domain"><Input /></Form.Item>
              <Form.Item name="country" label="Country"><Input /></Form.Item>
              <Form.Item name="city" label="City"><Input /></Form.Item>
              <Form.Item name="lead_source" label="Lead Source">
                <Select allowClear placeholder="Select source">
                  {LEAD_SOURCE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="lead_potential" label="Lead Potential">
                <Select allowClear placeholder="Select potential">
                  {LEAD_POTENTIAL_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="next_followup_at" label="Next Follow-up">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="therapy_interest" label="Therapy Interest">
              <Select mode="tags" placeholder="Add therapy areas" style={{ width: '100%' }}
                options={THERAPY_INTEREST_OPTIONS.map(o => ({ label: o, value: o }))}
              />
            </Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select a status' }]}>
              <Select placeholder="Select status" style={{ width: '100%' }}>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="bounces">Bounces</Select.Option>
                <Select.Option value="unsubscribes">Unsubscribes</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="tags" label="Tags">
              <Select
                mode="tags"
                placeholder="Add tags"
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const val = option?.value;
                  return typeof val === 'string' && val.toLowerCase().includes(input.toLowerCase());
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
              >
                {filterOptions.tags.map(tag => (
                  <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="notes" label="Notes"><TextArea rows={4} placeholder="Add notes" /></Form.Item>
            <Form.Item><Button type="primary" htmlType="submit" block>Update Lead</Button></Form.Item>
          </Form>
        )}
      </Drawer>

      {/* Import Modal */}
      <Modal title="Import Leads" open={importModalVisible}
        onCancel={resetImportState}
        footer={null} width={960}>
        <Tabs
          activeKey={importMode}
          onChange={(key) => {
            setImportMode(key as 'file' | 'manual');
            if (key === 'file') {
              setImportStep(0);
            }
          }}
          items={[
            {
              key: 'file',
              label: (
                <span>
                  <UploadOutlined /> Import File
                </span>
              ),
            },
            {
              key: 'manual',
              label: (
                <span>
                  <UserAddOutlined /> Add Manually
                </span>
              ),
            },
          ]}
        />

        {importMode === 'file' && (
          <Steps current={importStep} style={{ marginBottom: 24, marginTop: 16 }} items={[
            { title: 'Upload' },
            { title: 'Map Columns' },
            { title: 'Preview & Confirm' },
          ]} />
        )}

        {importMode === 'file' && (
          <>
        {/* Step 0: Upload */}
        {importStep === 0 && (
          <Spin spinning={uploadLoading} tip="Reading file and inspecting columns..." indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
            <Upload.Dragger
              accept=".xlsx,.xls,.csv"
              beforeUpload={handleFileUpload}
              fileList={importFile ? [{ uid: '1', name: importFile.name, status: 'done' as const }] : []}
              disabled={uploadLoading}
            >
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">Click or drag file to upload</p>
              <p className="ant-upload-hint">Supports Excel (.xlsx, .xls) and CSV (.csv) files</p>
            </Upload.Dragger>
          </Spin>
        )}

        {/* Step 1: Map Columns */}
        {importStep === 1 && columnInspection && (
          <div>
            <Text strong>Found {columnInspection.total_rows} rows and {columnInspection.columns.length} columns</Text>
            <div style={{ marginTop: 8, marginBottom: 16, padding: '8px 12px', background: isDark ? '#111a2c' : '#f0f5ff', borderRadius: 6, border: isDark ? '1px solid #1d39c4' : '1px solid #d6e4ff' }}>
              <InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
              <Text style={{ fontSize: 12 }}>
                Map each file column to a lead field. Sample values are shown below each column to help identify the data.
              </Text>
            </div>
            <Form form={mappingForm} layout="vertical" onFinish={handleColumnMapping} style={{ marginTop: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                {columnInspection.columns.map(col => {
                  const currentMappedField = mappedFields[col];
                  
                  // Build set of fields already taken by OTHER columns
                  const takenFields = new Set(
                    Object.entries(mappedFields)
                      .filter(([c, f]) => c !== col && f)
                      .map(([_, f]) => f)
                  );
                  
                  const isFieldTaken = (field: string) => {
                    if (!field || field === '') return false; // Skip is always available
                    return takenFields.has(field);
                  };

                  // Check if this column is mapped to email and samples don't look like emails
                  const emailSampleWarning = (() => {
                    if (currentMappedField !== 'email') return null;
                    const samples = columnInspection.column_samples[col]?.filter((s): s is string => s != null) || [];
                    if (samples.length === 0) return null;
                    const invalidSamples = samples.filter(s => !isValidEmail(s));
                    if (invalidSamples.length === 0) return null;
                    if (invalidSamples.length === samples.length) {
                      return { type: 'error' as const, message: 'None of the sample values look like email addresses. Are you sure this is the correct column?' };
                    }
                    return { type: 'warning' as const, message: `${invalidSamples.length} of ${samples.length} sample values don't look like valid emails: ${invalidSamples.join(', ')}` };
                  })();
                  
                  return (
                    <div key={col} style={{
                      marginBottom: 16,
                      padding: 12,
                      border: emailSampleWarning?.type === 'error' 
                        ? '1px solid #ff4d4f' 
                        : emailSampleWarning?.type === 'warning'
                          ? '1px solid #faad14'
                          : isDark ? '1px solid #434343' : '1px solid #f0f0f0',
                      borderRadius: 8,
                      background: isDark ? '#1f1f1f' : '#fff',
                    }}>
                      <Form.Item
                        label={
                          <Text strong style={{ fontSize: 13 }}>
                            {col}
                          </Text>
                        }
                        name={col}
                        style={{ marginBottom: 0 }}
                      >
                        <Select 
                          placeholder="Select field" 
                          allowClear 
                          style={{ width: '100%' }}
                          value={currentMappedField}
                          onChange={(value) => {
                            const newMappedFields = { ...mappedFields };
                            if (value && value !== '') {
                              newMappedFields[col] = value;
                            } else {
                              delete newMappedFields[col];
                            }
                            setMappedFields(newMappedFields);
                            mappingForm.setFieldValue(col, value);
                          }}
                        >
                          {Object.entries(FIELD_LABELS).map(([fieldValue, label]) => (
                            <Select.Option 
                              key={fieldValue} 
                              value={fieldValue} 
                              disabled={isFieldTaken(fieldValue)}
                            >
                              {label}{isFieldTaken(fieldValue) ? ' (already mapped)' : ''}
                            </Select.Option>
                          ))}
                          <Select.Option value="">Skip</Select.Option>
                        </Select>
                      </Form.Item>
                      {emailSampleWarning && (
                        <Alert
                          type={emailSampleWarning.type}
                          showIcon
                          icon={<ExclamationCircleOutlined />}
                          message={emailSampleWarning.message}
                          style={{ marginTop: 8, fontSize: 12 }}
                          banner
                        />
                      )}
                      {renderColumnSamples(col)}
                    </div>
                  );
                })}
              </div>
              <Space style={{ marginTop: 8 }}>
                <Button onClick={() => { 
                  setImportStep(0); 
                  mappingForm.resetFields(); 
                  setMappedFields({});
                }}>Back</Button>
                <Button type="primary" htmlType="submit">Continue to Preview</Button>
              </Space>
            </Form>
          </div>
        )}

        {/* Step 2: Preview & Confirm */}
        {importStep === 2 && columnInspection && (
          <div>
            <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>Preview & Confirm Import</Text>

            {/* Import-level metadata */}
            <Card size="small" title="Import Metadata (applied to all leads)" style={{ marginBottom: 16 }}>
              <Form form={metadataForm} layout="vertical" onValuesChange={(changed) => {
                if ('added_by' in changed) {
                  setAddedBySet(!!changed.added_by);
                }
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                  <Form.Item name="therapy_interest" label="Therapy Interest">
                    <Select mode="tags" placeholder="e.g. psoriasis, glaucoma"
                      options={THERAPY_INTEREST_OPTIONS.map(o => ({ label: o, value: o }))}
                    />
                  </Form.Item>
                  <Form.Item name="lead_source" label="Lead Source">
                    <Select allowClear placeholder="Select source">
                      {LEAD_SOURCE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="lead_potential" label="Lead Potential">
                    <Select allowClear placeholder="Select potential">
                      {LEAD_POTENTIAL_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="country" label="Country">
                    <Input placeholder="e.g. Germany" />
                  </Form.Item>
                  <Form.Item name="city" label="City">
                    <Input placeholder="e.g. Berlin" />
                  </Form.Item>
                  <Form.Item name="added_by" label="Added By" rules={[{ required: true, message: 'Added By is required' }]}>
                    <Select placeholder="Select person">
                      {ADDED_BY_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="tags" label="Tags" style={{ gridColumn: 'span 3' }}>
                    <Select mode="tags" placeholder="Add tags for all imported leads" />
                  </Form.Item>
                </div>
              </Form>
            </Card>

            {/* Invalid email detection */}
            {!loadingPreview && invalidEmails.length > 0 && (
              <Alert
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
                message={
                  <span>
                    <strong>{invalidEmails.length} invalid email{invalidEmails.length > 1 ? 's' : ''}</strong> found in preview (no @ or missing domain). These will be <strong>skipped</strong> during import.
                  </span>
                }
                description={
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      Fix them below or go back and map the correct column to Email.
                    </Text>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {invalidEmails.map((item, idx) => {
                        const correctedLower = item.corrected?.trim().toLowerCase();
                        const isFormatValid = !!item.corrected && isValidEmail(item.corrected);
                        const isDuplicate = isFormatValid && (
                          (previewData && (
                            previewData.db_duplicate_emails?.includes(correctedLower!) ||
                            previewData.file_duplicate_emails?.includes(correctedLower!)
                          )) ||
                          correctedDbDuplicates.has(correctedLower!)
                        );
                        const isOk = isFormatValid && !isDuplicate;

                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <Text type="danger" delete style={{ minWidth: 200, fontSize: 12 }}>{item.original}</Text>
                            <Input
                              size="small"
                              placeholder="Corrected email"
                              value={item.corrected || ''}
                              style={{ width: 280 }}
                              onChange={(e) => {
                                const updated = [...invalidEmails];
                                updated[idx] = { ...updated[idx], corrected: e.target.value };
                                setInvalidEmails(updated);
                                checkCorrectedEmailsInDb(updated);
                              }}
                              status={item.corrected ? (isOk ? '' : 'error') : undefined}
                            />
                            {isOk && (
                              <CheckOutlined style={{ color: '#52c41a' }} />
                            )}
                            {item.corrected && !isFormatValid && (
                              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                            )}
                            {isDuplicate && (
                              <Tooltip title="This email already exists in your database or file">
                                <Tag color="orange" style={{ margin: 0 }}>Duplicate</Tag>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
              />
            )}

            {/* Import summary */}
            {!loadingPreview && previewData && (() => {
              // Count invalid emails that are still unresolvable
              const invalidSkipCount = invalidEmails.filter(e => {
                if (!e.corrected || !isValidEmail(e.corrected)) return true;
                const correctedLower = e.corrected.trim().toLowerCase();
                if (previewData.db_duplicate_emails?.includes(correctedLower)) return true;
                if (previewData.file_duplicate_emails?.includes(correctedLower)) return true;
                if (correctedDbDuplicates.has(correctedLower)) return true;
                return false;
              }).length;
              const willImport = previewData.total_rows 
                - (previewData.db_duplicate_count || 0) 
                - (previewData.file_duplicate_count || 0) 
                - invalidSkipCount;

              return (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: isDark ? '#111a2c' : '#f0f5ff', borderRadius: 6, border: isDark ? '1px solid #1d39c4' : '1px solid #d6e4ff' }}>
                  <Space size="large">
                    <div><Text strong>Total rows:</Text> {previewData.total_rows}</div>
                    <div><Text strong>Will import:</Text> <Text type="success">{Math.max(0, willImport)}</Text></div>
                    {previewData.db_duplicate_count > 0 && (
                      <div><Text strong>DB duplicates (skip):</Text> <Text type="warning">{previewData.db_duplicate_count}</Text></div>
                    )}
                    {(previewData.file_duplicate_count || 0) > 0 && (
                      <div><Text strong>File duplicates (skip):</Text> <Text type="warning">{previewData.file_duplicate_count}</Text></div>
                    )}
                    {invalidSkipCount > 0 && (
                      <div><Text strong>Invalid emails (skip):</Text> <Text type="danger">{invalidSkipCount}</Text></div>
                    )}
                  </Space>
                </div>
              );
            })()}

            {/* Show column mappings */}
            <div style={{ marginBottom: 24, padding: 12, backgroundColor: isDark ? '#1f1f1f' : '#f5f5f5', borderRadius: 6, border: isDark ? '1px solid #434343' : 'none' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Column Mappings:</Text>
              {Object.entries(columnMapping).map(([ec, lf]) => (
                <div key={ec} style={{ marginBottom: 4 }}>
                  <Text>{ec}</Text> → <Text strong>{lf || 'Skip'}</Text>
                </div>
              ))}
            </div>

            {/* Show preview data table */}
            {loadingPreview ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                <div style={{ marginTop: 8 }}><Text>Loading preview and checking for duplicates...</Text></div>
              </div>
            ) : previewData && previewData.preview_rows.length > 0 ? (
              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>
                  Preview Data ({previewData.preview_rows.length} of {previewData.total_rows} rows):
                </Text>
                <Table
                  dataSource={previewData.preview_rows.map((row, idx) => ({ ...row, key: idx }))}
                  scroll={{ x: 'max-content', y: 500 }}
                  pagination={false}
                  columns={(() => {
                    // Group by lead field and only show the first mapping for each field
                    const fieldToExcelCol: Record<string, string> = {};
                    Object.entries(previewData.mapped_fields)
                      .filter(([_, field]) => field)
                      .forEach(([excelCol, leadField]) => {
                        // Only keep the first mapping for each field
                        if (!fieldToExcelCol[leadField]) {
                          fieldToExcelCol[leadField] = excelCol;
                        }
                      });
                    
                    // Create columns from unique fields
                    return Object.entries(fieldToExcelCol).map(([leadField, excelCol]) => ({
                      title: (
                        <span>
                          <Text strong>{leadField.charAt(0).toUpperCase() + leadField.slice(1).replace('_', ' ')}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>(from: {excelCol})</Text>
                        </span>
                      ),
                      dataIndex: leadField,
                      key: leadField,
                      render: (value: any, _record: any, index: number) => {
                        if (Array.isArray(value)) {
                          return value.join(', ');
                        }
                        if (leadField === 'email' && value) {
                          if (previewData.db_duplicate_emails?.includes(value.toLowerCase())) {
                            return <Text type="warning" style={{ textDecoration: 'line-through' }}>{value} (duplicate)</Text>;
                          }
                          if (!isValidEmail(value)) {
                            const correction = invalidEmails.find(e => e.row === index);
                            const correctedEmail = correction?.corrected?.trim();
                            const isCorrectedValid = correctedEmail && isValidEmail(correctedEmail);
                            const isCorrectedDuplicate = isCorrectedValid && (
                              previewData.db_duplicate_emails?.includes(correctedEmail!.toLowerCase()) ||
                              previewData.file_duplicate_emails?.includes(correctedEmail!.toLowerCase()) ||
                              correctedDbDuplicates.has(correctedEmail!.toLowerCase())
                            );
                            return (
                              <Tooltip title="Invalid email – will be skipped">
                                <Text type="danger" style={{ textDecoration: 'line-through' }}>
                                  <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                  {value}
                                </Text>
                                {isCorrectedValid && !isCorrectedDuplicate && (
                                  <Text type="success" style={{ display: 'block', fontSize: 11 }}>→ {correctedEmail}</Text>
                                )}
                                {isCorrectedDuplicate && (
                                  <Text type="warning" style={{ display: 'block', fontSize: 11 }}>→ {correctedEmail} (duplicate – will skip)</Text>
                                )}
                              </Tooltip>
                            );
                          }
                        }
                        return value || '-';
                      }
                    }));
                  })()}
                  size="small"
                  style={{ marginBottom: 16 }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 24, padding: 12, backgroundColor: isDark ? '#2b2111' : '#fffbe6', borderRadius: 6, border: isDark ? '1px solid #434343' : 'none' }}>
                <Text type="warning">No preview data available</Text>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button onClick={() => setImportStep(1)}>Back to Mapping</Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={handleCommitImport}
                size="large" loading={importLoading}
                disabled={loadingPreview}>
                {(() => {
                  if (!previewData) return 'Commit Import';
                  const invalidSkip = invalidEmails.filter(e => {
                    if (!e.corrected || !isValidEmail(e.corrected)) return true;
                    const cl = e.corrected.trim().toLowerCase();
                    if (previewData.db_duplicate_emails?.includes(cl)) return true;
                    if (previewData.file_duplicate_emails?.includes(cl)) return true;
                    if (correctedDbDuplicates.has(cl)) return true;
                    return false;
                  }).length;
                  const totalSkip = (previewData.db_duplicate_count || 0) + (previewData.file_duplicate_count || 0) + invalidSkip;
                  const willImport = Math.max(0, previewData.total_rows - totalSkip);
                  return totalSkip > 0
                    ? `Import ${willImport} Lead${willImport !== 1 ? 's' : ''} (Skip ${totalSkip})`
                    : 'Commit Import';
                })()}
              </Button>
              {!addedBySet && (
                <Text type="danger" style={{ fontSize: 13 }}>
                  <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                  Please set <strong>Added By</strong> above before importing
                </Text>
              )}
            </div>
          </div>
        )}
          </>
        )}

        {importMode === 'manual' && (
          <div style={{ marginTop: 16 }}>
            <Form form={manualForm} layout="vertical" onFinish={handleManualLeadSubmit}>
              <Alert
                type="info"
                showIcon
                message="Add a single lead manually"
                description="Fill in the lead information below. Email and Added By are required."
                style={{ marginBottom: 24 }}
              />
              
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input placeholder="example@company.com" />
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Form.Item name="first_name" label="First Name">
                  <Input placeholder="John" />
                </Form.Item>
                <Form.Item name="last_name" label="Last Name">
                  <Input placeholder="Doe" />
                </Form.Item>
                <Form.Item name="company" label="Company">
                  <Input placeholder="Company Name" />
                </Form.Item>
                <Form.Item name="role" label="Role">
                  <Input placeholder="Job Title" />
                </Form.Item>
                <Form.Item name="domain" label="Domain">
                  <Input placeholder="company.com" />
                </Form.Item>
                <Form.Item name="country" label="Country">
                  <Input placeholder="e.g. Germany" />
                </Form.Item>
                <Form.Item name="city" label="City">
                  <Input placeholder="e.g. Berlin" />
                </Form.Item>
                <Form.Item name="lead_source" label="Lead Source">
                  <Select allowClear placeholder="Select source">
                    {LEAD_SOURCE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="lead_potential" label="Lead Potential">
                  <Select allowClear placeholder="Select potential">
                    {LEAD_POTENTIAL_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </Form.Item>
                <Form.Item 
                  name="added_by" 
                  label="Added By"
                  rules={[
                    { required: true, message: 'Added By is required' },
                  ]}
                >
                  <Select allowClear placeholder="Select person">
                    {ADDED_BY_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </Form.Item>
              </div>

              <Form.Item name="therapy_interest" label="Therapy Interest">
                <Select mode="tags" placeholder="Add therapy areas" style={{ width: '100%' }}
                  options={THERAPY_INTEREST_OPTIONS.map(o => ({ label: o, value: o }))}
                />
              </Form.Item>

              <Form.Item name="tags" label="Tags">
                <Select mode="tags" placeholder="Add tags" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="notes" label="Notes">
                <TextArea rows={4} placeholder="Add any additional notes" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button onClick={resetImportState}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={manualLoading}>
                    Add Lead
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Bulk Edit Modal */}
      <Modal
        title={`Bulk Edit ${selectedRowKeys.length} Lead${selectedRowKeys.length !== 1 ? 's' : ''}`}
        open={bulkEditModalVisible}
        onCancel={() => { setBulkEditModalVisible(false); setBulkEditField(undefined); bulkEditForm.resetFields(); }}
        onOk={handleBulkEdit}
        okText="Apply to All Selected"
        okButtonProps={{ loading: bulkEditLoading, disabled: !bulkEditField }}
        width={520}
      >
        <Alert
          type="info"
          showIcon
          message="Select a field and set a value. This value will be applied to all selected leads."
          style={{ marginBottom: 20 }}
        />

        <Form form={bulkEditForm} layout="vertical">
          <Form.Item label="Field to Update">
            <Select
              placeholder="Choose a field..."
              value={bulkEditField}
              onChange={(val) => {
                setBulkEditField(val);
                bulkEditForm.resetFields();
              }}
              style={{ width: '100%' }}
            >
              {BULK_EDIT_FIELDS.map(f => (
                <Select.Option key={f.value} value={f.value}>{f.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          {bulkEditField === 'therapy_interest' && (
            <Form.Item name="therapy_interest" label="Therapy Interest">
              <Select mode="tags" placeholder="Select or type therapy areas" style={{ width: '100%' }}
                options={THERAPY_INTEREST_OPTIONS.map(o => ({ label: o, value: o }))}
              />
            </Form.Item>
          )}

          {bulkEditField === 'lead_potential' && (
            <Form.Item name="lead_potential" label="Lead Potential">
              <Select placeholder="Select potential" style={{ width: '100%' }}>
                {LEAD_POTENTIAL_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>
          )}

          {bulkEditField === 'lead_source' && (
            <Form.Item name="lead_source" label="Lead Source">
              <Select placeholder="Select source" style={{ width: '100%' }}>
                {LEAD_SOURCE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>
          )}

          {bulkEditField === 'added_by' && (
            <Form.Item name="added_by" label="Added By">
              <Select placeholder="Select person" style={{ width: '100%' }}>
                {ADDED_BY_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>
          )}

          {bulkEditField === 'country' && (
            <Form.Item name="country" label="Country">
              <Input placeholder="e.g. Germany" />
            </Form.Item>
          )}

          {bulkEditField === 'city' && (
            <Form.Item name="city" label="City">
              <Input placeholder="e.g. Berlin" />
            </Form.Item>
          )}

          {bulkEditField === 'company' && (
            <Form.Item name="company" label="Company">
              <Input placeholder="e.g. Sun Pharma" />
            </Form.Item>
          )}

          {bulkEditField === 'domain' && (
            <Form.Item name="domain" label="Domain">
              <Input placeholder="e.g. sunpharma.com" />
            </Form.Item>
          )}

          {bulkEditField === 'tags' && (
            <Form.Item name="tags" label="Tags">
              <Select
                mode="tags"
                placeholder="Add tags"
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const val = option?.value;
                  return typeof val === 'string' && val.toLowerCase().includes(input.toLowerCase());
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
              >
                {filterOptions.tags.map(tag => (
                  <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {bulkEditField === 'status' && (
            <Form.Item name="status" label="Status">
              <Select placeholder="Select status" style={{ width: '100%' }}>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="bounces">Bounces</Select.Option>
                <Select.Option value="unsubscribes">Unsubscribes</Select.Option>
              </Select>
            </Form.Item>
          )}

          {bulkEditField === 'next_followup_at' && (
            <Form.Item name="next_followup_at" label="Next Follow-up Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}
        </Form>

        {bulkEditField && (
          <div style={{
            marginTop: 8,
            padding: 12,
            background: isDark ? '#2b2111' : '#fffbe6',
            borderRadius: 6,
            border: isDark ? '1px solid #434343' : '1px solid #ffe58f',
          }}>
            <Text type="warning" style={{ fontSize: 13 }}>
              <ExclamationCircleOutlined style={{ marginRight: 6 }} />
              This will overwrite the <strong>{BULK_EDIT_FIELDS.find(f => f.value === bulkEditField)?.label}</strong> value for all {selectedRowKeys.length} selected lead{selectedRowKeys.length !== 1 ? 's' : ''}.
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};
